import { lazy, Suspense, useRef, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, Loader2, Play, Radio, Send, Square, Trash2, Upload } from 'lucide-react'
import { g100AudioApi, g100ConsoleApi, g100LiveApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { isConsoleOff, useG100ConsoleCameras } from '@/hooks/useG100Console'
import { AddDeviceCard } from '@/components/g100Console/AddDeviceCard'
import { FirmwareCard } from '@/components/g100Console/FirmwareCard'
import { TypedNameDialog } from '@/components/g100Console/TypedNameDialog'
import { SettingsCard } from '@/components/g100Console/SettingsCard'
import { otaText } from '@/components/g100Console/otaResult'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { G100_SPECIES } from '@/types'
import type {
  G100AudioCommand, G100AudioDeleteRequest, G100AudioDownloadRequest, G100AudioFile, G100AudioLimits,
  G100AudioOnCamera, G100AudioOverview, G100AudioPackage, G100AudioPlaybackRequest, G100AudioSpeciesRequest,
  G100ConsoleAction, G100ConsoleCamera,
  G100ConsoleCommand, G100ConsoleInfo, G100ConsoleMessage, G100ConsoleSendRequest, G100ConsoleStatus,
  G100LiveStartRequest,
  G100PackageLoadResult, G100Presence, G100Species,
} from '@/types'

// #309 — G100 Test Console (stage only). A G100's owner (or a superuser) sends any protocol
// command, or a wake-up on its own, and watches the results and the camera's message log.
// The API is the real gate: where the console is off it answers 404 and this page leaves.
// Commands go through the server's normal queue, so the automatic wake-up still goes first.
// #310 — the Audio section sends real sound files: the owner's library (upload a WAV, the server
// checks it and computes its size and CRC32) and the four audio commands (V1.1 6.12.20-22, V1.4 6.12.23), whose results
// arrive in the same Results and message log as every other command.
// #380 — V1.4 6.12.23 audioPlayback: Play on every sound the camera reported (or one typed id) and
// Stop, from the same Audio section; the camera's status (0, 1, 2) is the answer under Results.
// #381 — liveAction from the Commands card: a form of the four testable values (start/stop, session length,
// idle period, codec); Send opens the Live view card, whose own viewer makes the one start request with those
// values. The server adds region, channel, keys and expiry (#311); there is no JSON box to paste a key into (#358).

// #311 L6 — the live panel and the KVS SDK load only when the Live card is opened and Start is pressed.
const LivePanel = lazy(() => import('@/components/g100Live/LivePanel'))
import type { LiveStartRequest } from '@/components/g100Live/LivePanel'

const PAGE_SIZE = 50

export function G100ConsolePage() {
  const { t } = useTranslation('g100Console')
  const cameras = useG100ConsoleCameras()
  const [cameraId, setCameraId] = useState<number | null>(null)

  if (cameras.isError && isConsoleOff(cameras.error)) return <Navigate to="/" replace />
  if (cameras.isLoading) return <p className="p-4 text-sm text-muted-foreground">{t('loading')}</p>
  if (cameras.isError) return <p className="p-4 text-sm text-destructive">{t('loadError')}</p>

  const list = cameras.data?.results ?? []
  const camera = list.find((c) => c.id === cameraId) ?? list[0] ?? null

  return (
    <div className="space-y-4 p-4">
      {/* #397: the card shows on the empty page too; only a superuser can add while owning no G100 camera (the server answers 403 otherwise and the card shows that reason). */}
      <AddDeviceCard onAdded={(added) => setCameraId(added.id)} />
      {camera === null ? (
        <p className="text-sm text-muted-foreground">{t('noCameras')}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="g100-console-camera" className="text-sm font-medium">{t('picker.label')}</label>
            <select
              id="g100-console-camera"
              value={camera.id}
              onChange={(e) => setCameraId(Number(e.target.value))}
              className="rounded border border-border bg-background px-2 py-1 text-sm"
            >
              {list.map((c) => <option key={c.id} value={c.id}>{cameraLabel(c)}</option>)}
            </select>
            <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">
              {t('stageOnly')}
            </span>
          </div>
          {/* Keyed: every draft, page and dialog belongs to one camera. */}
          <CameraConsole key={camera.id} cameraId={camera.id} />
        </>
      )}
    </div>
  )
}

function cameraLabel(c: G100ConsoleCamera) {
  return `${c.name} (${c.imei})`
}

function CameraConsole({ cameraId }: { cameraId: number }) {
  const { t } = useTranslation('g100Console')
  // #381: the Live view card's open state and the start request the Commands card hands it. A new
  // token per Send: the panel starts once per token.
  const [live, setLive] = useState<{ opened: boolean; request: LiveStartRequest | null }>({ opened: false, request: null })
  const openLive = () => setLive((s) => ({ ...s, opened: true }))
  const startLive = (body: G100LiveStartRequest) =>
    setLive((s) => ({ opened: true, request: { token: (s.request?.token ?? 0) + 1, body } }))
  const info = useQuery({
    queryKey: queryKeys.g100Console.info(cameraId),
    queryFn: () => g100ConsoleApi.info(cameraId),
    ...queryOptions.g100Console.info,
  })

  return (
    <div className="space-y-4">
      {info.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('loading')}</p>
      ) : info.isError || !info.data ? (
        <p className="text-sm text-destructive">{t('status.loadError')}</p>
      ) : (
        <>
          <StatusCard status={info.data.status} />
          {/* #379: every setting of 6.14.4, editable; the factory's 21 Oct request. */}
          <SettingsCard cameraId={cameraId} />
          <LiveCard cameraId={cameraId} cameraName={info.data.camera.name} opened={live.opened} onOpen={openLive} startRequest={live.request} />
          <div className="grid gap-4 lg:grid-cols-2">
            <ActionsCard cameraId={cameraId} info={info.data} onLiveStart={startLive} />
            <ResultsCard cameraId={cameraId} />
          </div>
          <FirmwareCard cameraId={cameraId} cameraName={info.data.camera.name} />
        </>
      )}
      <AudioCard cameraId={cameraId} />
      <MessageLog cameraId={cameraId} />
    </div>
  )
}

// ---- live view (#311) ------------------------------------------------------------------

function LiveCard({ cameraId, cameraName, opened, onOpen, startRequest }: {
  cameraId: number
  cameraName: string
  opened: boolean
  onOpen: () => void
  startRequest: LiveStartRequest | null     // #381
}) {
  const { t } = useTranslation('g100Console')
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('live.title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('live.hint')}</p>
      </CardHeader>
      <CardContent>
        {opened ? (
          <Suspense fallback={<p className="text-sm text-muted-foreground">{t('loading')}</p>}>
            <LivePanel cameraId={cameraId} cameraName={cameraName} variant="console" startRequest={startRequest} />
          </Suspense>
        ) : (
          <Button size="sm" variant="outline" onClick={onOpen}>{t('live.open')}</Button>
        )}
      </CardContent>
    </Card>
  )
}

// ---- status ----------------------------------------------------------------------------

const PRESENCE_STYLE: Record<G100Presence, string> = {
  online: 'bg-green-500/15 text-green-700 dark:text-green-400',
  asleep: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  overdue: 'bg-red-500/15 text-red-700 dark:text-red-400',
  never: 'bg-muted text-muted-foreground',
}

function when(iso: string | null) {
  return iso ? new Date(iso).toLocaleString() : null
}

// The five audio package statuses V1.5 defines; any other value is shown as the camera sent it.
const PACKAGE_STATUSES = [
  'AUDIO_PACKAGE_LOAD_SUCCESS', 'AUDIO_PACKAGE_LOAD_FAILED', 'AUDIO_PACKAGE_DOWNLOAD_SUCCESS',
  'AUDIO_PACKAGE_DOWNLOAD_FAILED', 'AUDIO_PACKAGE_DOWNLOAD_URL_INVALID',
] as const
const PACKAGE_STATUS: ReadonlySet<string> = new Set(PACKAGE_STATUSES)

function StatusCard({ status }: { status: G100ConsoleStatus }) {
  const { t } = useTranslation('g100Console')
  const none = t('status.none')
  const mode = status.work_mode === 1 || status.work_mode === 2 || status.work_mode === 3
    ? t(`status.workModes.${String(status.work_mode) as '1' | '2' | '3'}`)
    : null
  const sd = status.sd_capacity_mb !== null && status.sd_free_mb !== null
    ? t('status.sdValue', { free: status.sd_free_mb, total: status.sd_capacity_mb })
    : null
  const d = status.daily_report
  const report = d
    ? t('status.dailyReportValue', {
        when: when(d.received_at), triggers: d.triggerTimes ?? none,
        thumbOk: d.sendThumbnailSuccTimes ?? none, thumbErr: d.sendThumbnailErrTimes ?? none,
        hdOk: d.sendHDSuccTimes ?? none, hdErr: d.sendHDErrTimes ?? none,
      })
    : null
  const rows: [string, string | null][] = [
    [t('status.lastSeen'), when(status.last_seen_at)],
    [t('status.lastLogin'), when(status.last_login_at)],
    [t('status.nextCheckin'), when(status.next_checkin_expected)],
    [t('status.battery'), status.battery !== null ? `${status.battery}%` : null],
    [t('status.sd'), sd],
    [t('status.firmware'), status.firmware],
    [t('status.moduleFirmware'), status.module_firmware],
    [t('status.displayVersion'), status.display_version],
    [t('status.model'), status.model],
    [t('status.dailyReport'), report],
    [t('status.signal'), status.signal_percent !== null ? `${status.signal_percent}%` : null],
    [t('status.workMode'), mode],
  ]
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {t('status.title')}
          <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', PRESENCE_STYLE[status.presence])}>
            {t(`status.presence.${status.presence}`)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-2 border-b border-border/40 py-1">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-right font-mono text-xs">{value ?? none}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

// ---- actions ---------------------------------------------------------------------------

function pretty(value: unknown) {
  return JSON.stringify(value, null, 2)
}

/** The server's reason for a refusal, in one line. */
function reason(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: Record<string, unknown>; detail?: unknown } } })
    ?.response?.data
  if (data?.errors) return Object.entries(data.errors).map(([k, v]) => `${k}: ${String(v)}`).join('; ')
  if (data?.detail) return String(data.detail)
  return ''
}

function ActionsCard({ cameraId, info, onLiveStart }: {
  cameraId: number
  info: G100ConsoleInfo
  onLiveStart: (body: G100LiveStartRequest) => void      // #381
}) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  const actions = info.actions
  const [selected, setSelected] = useState(
    (actions.find((a) => a.supported) ?? actions[0])?.code ?? '')
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [paramsError, setParamsError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<G100ConsoleSendRequest | null>(null)

  const action = actions.find((a) => a.code === selected)
  const draft = action ? drafts[action.code] ?? pretty(action.params) : ''

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.commands(cameraId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.messagesAll(cameraId) })
  }
  const failed = (error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status
    toast.error(t('actions.sendFailed'), status === 429 ? t('actions.throttled') : reason(error))
  }

  const send = useMutation({
    mutationFn: (body: G100ConsoleSendRequest) => g100ConsoleApi.send(cameraId, body),
    onSuccess: (row) => {
      setConfirming(null)
      toast.success(t('actions.sent', { action: row.action }))
      refresh()
    },
    onError: failed,
  })
  const wake = useMutation({
    mutationFn: () => g100ConsoleApi.wake(cameraId),
    onSuccess: () => {
      toast.success(t('actions.wakeQueued'))
      refresh()
    },
    onError: failed,
  })

  const onSend = () => {
    if (!action) return
    let params: unknown
    try {
      params = JSON.parse(draft)
    } catch {
      setParamsError(t('actions.invalidJson'))
      return
    }
    if (params === null || typeof params !== 'object' || Array.isArray(params)) {
      setParamsError(t('actions.notObject'))
      return
    }
    setParamsError(null)
    const body = { action: action.code, params: params as Record<string, unknown> }
    if (action.dangerous) setConfirming(body)
    else send.mutate(body)
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('actions.title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('actions.hint')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 rounded border border-border/60 p-2">
          <Button size="sm" variant="outline" onClick={() => wake.mutate()} disabled={wake.isPending}>
            {wake.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Radio className="mr-2 h-4 w-4" />}
            {t('actions.wake')}
          </Button>
          <span className="break-all text-xs text-muted-foreground">
            {t('actions.wakeHint', { topic: info.wake_topic })}
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
          <ul className="max-h-80 space-y-0.5 overflow-y-auto pr-1">
            {actions.map((a) => (
              <li key={a.code}>
                <button
                  type="button"
                  onClick={() => { setSelected(a.code); setParamsError(null) }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left font-mono text-xs',
                    a.code === selected ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                    ((!a.supported && a.unavailable !== 'live_streaming') || a.struck) && 'text-muted-foreground',
                  )}
                >
                  <span className="truncate">{a.code}</span>
                  {a.dangerous && <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-destructive" aria-label={t('actions.dangerous')} />}
                </button>
              </li>
            ))}
          </ul>

          {action && (
            <ActionDetail
              cameraId={cameraId}
              action={action}
              draft={draft}
              error={paramsError}
              sending={send.isPending}
              onDraft={(text) => setDrafts((d) => ({ ...d, [action.code]: text }))}
              onSend={onSend}
              onLiveStart={onLiveStart}
            />
          )}
        </div>
      </CardContent>

      {/* Remounted on open and close, so the typed name never carries over. */}
      <TypedNameDialog
        key={confirming ? 'open' : 'closed'}
        request={confirming}
        cameraName={info.camera.name}
        sending={send.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={(name) => confirming && send.mutate({ ...confirming, confirm_name: name })}
      />
    </Card>
  )
}

function ActionDetail({ cameraId, action, draft, error, sending, onDraft, onSend, onLiveStart }: {
  cameraId: number
  action: G100ConsoleAction
  draft: string
  error: string | null
  sending: boolean
  onDraft: (text: string) => void
  onSend: () => void
  onLiveStart: (body: G100LiveStartRequest) => void
}) {
  const { t } = useTranslation('g100Console')
  const heading = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-sm font-semibold">{action.code}</span>
      {action.section && (
        <span className="text-xs text-muted-foreground">{t('actions.section', { section: action.section })}</span>
      )}
      {action.dangerous && (
        <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-xs text-destructive">{t('actions.dangerous')}</span>
      )}
    </div>
  )
  // #358: a command that cannot be sent from here offers nothing to type: the name, the section and
  // one note saying where it comes from. (The factory had pasted a real liveAction message, with a
  // temporary AWS key, into the box that showed the protocol's example.)
  // #381: liveAction is a form of the four testable values; the server adds the AWS parts (#311).
  if (action.unavailable === 'live_streaming') {
    return <LiveActionForm cameraId={cameraId} heading={heading} onLiveStart={onLiveStart} />
  }
  if (action.unavailable) {
    return (
      <div className="min-w-0 space-y-2">
        {heading}
        <p className="rounded bg-muted px-2 py-1 text-xs">{t(`actions.unavailable.${action.unavailable}`)}</p>
      </div>
    )
  }
  return (
    <div className="min-w-0 space-y-2">
      {heading}
      {/* #358: struck through in V1.1 and V1.2. Still sent, so the camera's refusal can be tested. */}
      {action.struck && (
        <p className="rounded bg-muted px-2 py-1 text-xs">{t('actions.struckOut')}</p>
      )}
      <ul className="space-y-0.5 text-xs text-muted-foreground">
        <li>{action.from_example ? t('actions.fromExample') : t('actions.fromTable')}</li>
        {action.dangerous && <li>{t('actions.dangerousHint')}</li>}
        {action.no_result && <li>{t('actions.noResult')}</li>}
        {action.server_built && <li>{t('actions.serverBuilt')}</li>}
        {action.code === 'audioDownload' && <li>{t('actions.audioHint')}</li>}
        {action.code === 'otaUpdate' && <li>{t('actions.otaHint')}</li>}
        <li>{t('actions.noSecrets')}</li>
      </ul>
      <label htmlFor="g100-console-params" className="block text-xs font-medium">{t('actions.params')}</label>
      <textarea
        id="g100-console-params"
        value={draft}
        onChange={(e) => onDraft(e.target.value)}
        spellCheck={false}
        rows={Math.min(14, Math.max(4, draft.split('\n').length + 1))}
        className="w-full rounded border border-border bg-background p-2 font-mono text-xs"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <Button size="sm" onClick={onSend} disabled={!action.supported || sending}>
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('actions.send')}
      </Button>
    </div>
  )
}

/** A whole number typed in a box, or null. */
function wholeNumber(text: string): number | null {
  return /^\d{1,7}$/.test(text.trim()) ? Number(text.trim()) : null
}

class NoLiveSession extends Error {}

type LiveFields = { maxLength: string; idle: string; codec: 'H264' | 'H265' }

// #381 — liveAction (6.12.19) from the Commands card: Start or Stop, the session length, the idle period and
// the codec; pre-filled from the server's defaults (GET live/). Send with Start opens the Live view card, whose
// viewer makes the one start request with these values (no second viewer); with a session already open the
// values are read-only and Start joins it. Send with Stop ends the open session through the live API (the same
// endpoint as the card's "Stop for everyone"), or says that none is open.
function LiveActionForm({ cameraId, heading, onLiveStart }: {
  cameraId: number
  heading: ReactNode
  onLiveStart: (body: G100LiveStartRequest) => void
}) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  // Polled (review): the session can open or end from the Live card, another viewer or the camera; the
  // form follows it, and Send re-reads it before a start.
  const status = useQuery({
    queryKey: queryKeys.g100Console.liveStatus(cameraId),
    queryFn: () => g100LiveApi.status(cameraId),
    refetchInterval: queryOptions.g100Console.liveStatusMs,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const defaults = status.data?.defaults ?? null
  const open = status.data?.session ?? null
  const enabled = status.data ? status.data.enabled : true
  const unreadable = status.isError
  const [kind, setKind] = useState<'0' | '1'>('0')
  const [typed, setTyped] = useState<LiveFields | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fromValues = (v: { maximumSessionLength: number; idlePeriod: number; codec: 'H264' | 'H265' }): LiveFields =>
    ({ maxLength: String(v.maximumSessionLength), idle: String(v.idlePeriod), codec: v.codec })
  const shown: LiveFields = open?.params
    ? fromValues(open.params)
    : typed ?? (defaults ? fromValues(defaults) : { maxLength: '', idle: '', codec: 'H264' })
  const edit = (patch: Partial<LiveFields>) => { setTyped({ ...shown, ...patch }); setError(null) }
  const locked = open !== null || !enabled || unreadable || kind === '1'
  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.liveStatus(cameraId) })

  const stop = useMutation({
    mutationFn: async () => {
      const fresh = await g100LiveApi.status(cameraId)      // the session may have ended meanwhile
      if (!fresh.session) throw new NoLiveSession()
      await g100LiveApi.stop(cameraId, fresh.session.id)
    },
    onSuccess: () => {
      toast.success(t('actions.live.stopped'))
      refreshStatus()
    },
    onError: (err) => {
      if (err instanceof NoLiveSession) toast.error(t('actions.live.noSession'))
      else toast.error(t('actions.sendFailed'), refusal(err, t('actions.throttled')))
    },
  })
  // Start: re-read the status first (review): a session opened since the form loaded is joined, and its
  // values stay; otherwise the typed values are checked and handed to the Live card's viewer.
  const start = useMutation({
    mutationFn: async () => {
      const fresh = await g100LiveApi.status(cameraId)
      if (fresh.session) {
        onLiveStart({})
        toast.success(t('actions.live.joinNote'))
        return
      }
      const cap = fresh.defaults.maximumSessionLength
      const length = wholeNumber(shown.maxLength)
      if (length === null || length < 60 || length > cap) {
        setError(t('actions.live.badValue', { field: t('actions.live.maxLength'), rule: t('actions.live.ruleLength', { min: 60, max: cap }) }))
        return
      }
      const idle = wholeNumber(shown.idle)
      if (idle === null || idle < 1 || idle > 3600) {
        setError(t('actions.live.badValue', { field: t('actions.live.idle'), rule: t('actions.live.ruleIdle', { min: 1, max: 3600 }) }))
        return
      }
      onLiveStart({ maximumSessionLength: length, idlePeriod: idle, codec: shown.codec })
    },
    onSettled: refreshStatus,
    onError: (err) => toast.error(t('actions.sendFailed'), refusal(err, t('actions.throttled'))),
  })

  const onSend = () => {
    setError(null)
    if (kind === '1') stop.mutate()
    else start.mutate()
  }
  const busy = stop.isPending || start.isPending

  const field = 'rounded border border-border bg-background px-2 py-1 font-mono text-xs disabled:opacity-60'
  return (
    <div className="min-w-0 space-y-2">
      {heading}
      <p className="rounded bg-muted px-2 py-1 text-xs">{t('actions.unavailable.live_streaming')}</p>
      {unreadable && <p className="text-xs text-destructive">{t('actions.live.statusFailed', { reason: reason(status.error) })}</p>}
      {!enabled && <p className="text-xs text-destructive">{t('actions.live.notEnabled')}</p>}
      {open && <p className="text-xs text-muted-foreground">{t('actions.live.joinNote')}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs font-medium">
          {t('actions.live.type')}
          <select value={kind} onChange={(e) => { setKind(e.target.value as '0' | '1'); setError(null) }} disabled={!enabled} className={field}>
            <option value="0">{t('actions.live.start')}</option>
            <option value="1">{t('actions.live.stop')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          {t('actions.live.codec')}
          <select value={shown.codec} onChange={(e) => edit({ codec: e.target.value as 'H264' | 'H265' })} disabled={locked} className={field}>
            <option value="H264">H264</option>
            <option value="H265">H265</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          {t('actions.live.maxLength')}
          <input type="number" inputMode="numeric" value={shown.maxLength} onChange={(e) => edit({ maxLength: e.target.value })} disabled={locked} className={field} aria-describedby={error ? 'g100-live-error' : undefined} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          {t('actions.live.idle')}
          <input type="number" inputMode="numeric" value={shown.idle} onChange={(e) => edit({ idle: e.target.value })} disabled={locked} className={field} aria-describedby={error ? 'g100-live-error' : undefined} />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">{t('actions.live.hint')}</p>
      {error && <p id="g100-live-error" className="text-xs text-destructive">{error}</p>}
      <Button size="sm" onClick={onSend} disabled={!enabled || unreadable || status.isLoading || busy}>
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('actions.send')}
      </Button>
    </div>
  )
}

// ---- audio (#310) ----------------------------------------------------------------------

// The raw form above can still send the audio commands by hand; audioDownload's hint points here,
// where real files are sent (review M5: only audioDownload sends a file).
const MB = 1024 * 1024

function statusOf(error: unknown) {
  return (error as { response?: { status?: number } })?.response?.status
}

/** Why the server refused a command, or that it was throttled. */
function refusal(error: unknown, throttled: string) {
  return statusOf(error) === 429 ? throttled : reason(error)
}

/** The server's reason for refusing an upload: its message about the file, else any reason. */
function fileReason(error: unknown): string {
  const file = (error as { response?: { data?: { errors?: { file?: unknown } } } })?.response?.data?.errors?.file
  return typeof file === 'string' ? file : reason(error)
}

/** After a queued audio command: the console's own Results and message log pick it up. */
function useAudioCommandSent(cameraId: number) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  return {
    onSuccess: (row: G100AudioCommand) => {
      toast.success(t('actions.sent', { action: row.action }))
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.commands(cameraId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.messagesAll(cameraId) })
    },
    onError: (error: unknown) => toast.error(t('actions.sendFailed'), refusal(error, t('actions.throttled'))),
  }
}

function AudioCard({ cameraId }: { cameraId: number }) {
  const { t } = useTranslation('g100Console')
  const audio = useQuery({
    queryKey: queryKeys.g100Console.audio(cameraId),
    queryFn: () => g100AudioApi.get(cameraId),
    ...queryOptions.g100Console.audio,
  })
  const why = audio.isError ? reason(audio.error) : ''
  // #380 audioPlayback (V1.4 6.12.23). "Last played" is the id of the last Play queued from this
  // page (table or typed), so Stop carries the id of the sound that is most likely playing. A ref,
  // not state: nothing is drawn from it, and Stop reads it at the click, never from a stale render.
  const lastPlayed = useRef<number | null>(null)
  const sent = useAudioCommandSent(cameraId)
  const playback = useMutation({
    mutationFn: (body: G100AudioPlaybackRequest) => g100AudioApi.playback(cameraId, body),
    onSuccess: (row, body) => {
      if (body.type === 1) lastPlayed.current = body.audioId
      sent.onSuccess(row)
    },
    onError: sent.onError,
  })
  const play = (audioId: number) => playback.mutate({ type: 1, audioId })
  // Stop: the last played from here, else the first sound the camera reported, else 1.
  const stop = (fallback: number) => playback.mutate({ type: 0, audioId: lastPlayed.current ?? fallback })
  // Review: the body in flight, so only the button that sent it spins; every Play/Stop is disabled meanwhile.
  const inFlight = playback.isPending ? (playback.variables ?? null) : null

  return (
    <Card>
      <section aria-label={t('audio.title')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('audio.title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('audio.hint')}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {audio.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : (
            <>
              {/* Review M1: a failed refresh keeps the last data (and what was ticked, chosen and
                  typed) on screen, with the error above it. Both keep their place, so nothing remounts. */}
              {audio.isError && (
                <div className="space-y-1">
                  <p className="text-sm text-destructive">{t('audio.loadError')}</p>
                  {why && <p className="text-xs text-muted-foreground">{why}</p>}
                </div>
              )}
              {audio.data && (
                <>
                  <AudioPackage pkg={audio.data.package} limits={audio.data.limits} />
                  <AudioLibrary cameraId={cameraId} overview={audio.data} />
                  <div className="grid gap-6 lg:grid-cols-2">
                    <AudioOnCamera onCamera={audio.data.on_camera} onPlay={play} onStop={stop} inFlight={inFlight} />
                    <AudioByIds cameraId={cameraId} limits={audio.data.limits} onPlay={play} inFlight={inFlight} />
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </section>
    </Card>
  )
}

function SpeciesSelect({ value, onChange, empty, id, label }: {
  value: G100Species | ''
  onChange: (value: G100Species | '') => void
  empty: string                     // the text of the "no species" choice
  id?: string                       // with a <label htmlFor> outside
  label?: string                    // or an accessible name of its own
}) {
  return (
    <select
      id={id}
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as G100Species | '')}
      className="rounded border border-border bg-background px-1.5 py-1 text-xs"
    >
      <option value="">{empty}</option>
      {G100_SPECIES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  )
}

/** V1.3 section 11: the default package a camera downloads at login when its TF card holds no audio. */
const PACKAGE_FILES = 50

// pkg is undefined in an answer from before 372 (or one still cached): the block is left out
// rather than let a TypeError reach the app's ErrorBoundary.
function AudioPackage({ pkg, limits }: { pkg: G100AudioPackage | undefined; limits: G100AudioLimits }) {
  const { t } = useTranslation('g100Console')
  if (!pkg) return null
  const size = { files: pkg.files, max: PACKAGE_FILES, mb: (pkg.bytes / MB).toFixed(1) }
  const changed = when(pkg.updated_at)
  return (
    <div className="min-w-0 space-y-2">
      <h3 className="text-sm font-semibold">{t('audio.package.title')}</h3>
      {pkg.files === 0 ? (
        <p className="text-sm text-muted-foreground">{t('audio.package.empty')}</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {changed ? t('audio.package.summaryWhen', { ...size, when: changed }) : t('audio.package.summary', size)}
          </p>
          <p className="text-xs text-muted-foreground">{t('audio.package.note')}</p>
        </>
      )}
      <PackageLoader limits={limits} hasFiles={pkg.files > 0} />
      {pkg.files > 0 && (
        <>
          <div className="max-h-72 overflow-auto rounded border border-border">
            <table className="w-full text-xs" aria-label={t('audio.package.title')}>
              <thead className="sticky top-0 bg-muted text-left">
                <tr>
                  <th className="px-2 py-2">{t('audio.package.slot')}</th>
                  <th className="px-2 py-2">{t('audio.package.species')}</th>
                  <th className="px-2 py-2">{t('audio.package.name')}</th>
                  <th className="px-2 py-2">{t('audio.package.length')}</th>
                </tr>
              </thead>
              <tbody>
                {pkg.slots.map((slot) => (
                  <tr key={slot.slot} className="border-t border-border">
                    <td className="px-2 py-1.5 font-mono">{String(slot.slot).padStart(2, '0')}</td>
                    <td className="px-2 py-1.5">{slot.species}</td>
                    <td className="break-all px-2 py-1.5 font-mono">{slot.name}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono">
                      {t('audio.package.seconds', { s: (slot.duration_ms / 1000).toFixed(1) })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// #383: the server's cap for one load (audio.PACKAGE_UPLOAD_MAX_BYTES): 50 files of 30 s are 72 MB.
const PACKAGE_UPLOAD_MAX_BYTES = 80 * MB

/** The server's reason for refusing a package load: its message about the files, else any reason. */
function filesReason(error: unknown): string {
  const files = (error as { response?: { data?: { errors?: { files?: unknown } } } })?.response?.data?.errors?.files
  return typeof files === 'string' ? files : reason(error)
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Build review N8: after the click has handed the file to the browser (some browsers lose it otherwise).
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/** The reason in the error body of a blob request, which arrives as a Blob, not as JSON. */
async function blobReason(error: unknown): Promise<string> {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (!(data instanceof Blob)) return reason(error)
  try {
    const body = JSON.parse(await data.text()) as { detail?: unknown; errors?: unknown }
    return reason({ response: { data: body } })
  } catch {
    return ''
  }
}

const slotList = (slots: number[]) => slots.map((s) => String(s).padStart(2, '0')).join(', ')

/** #383: load files into the default package (one audio.tar, or WAV files named by slot), or download it.
 *  The other slots are kept unless the box is ticked; the package is one for every camera, so every
 *  camera's Audio data is refreshed after a load. */
function PackageLoader({ limits, hasFiles }: { limits: G100AudioLimits; hasFiles: boolean }) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  const [chosen, setChosen] = useState<File[]>([])
  const [removeOthers, setRemoveOthers] = useState(false)
  const [inputKey, setInputKey] = useState(0)        // remounts the file input to clear it
  const [error, setError] = useState<string | null>(null)
  const [notes, setNotes] = useState<string[]>([])

  const load = useMutation({
    mutationFn: (body: { files: File[]; removeOthers: boolean }) =>
      g100ConsoleApi.loadPackage(body.files, body.removeOthers),
    onSuccess: (result: G100PackageLoadResult) => {
      setChosen([])
      setInputKey((k) => k + 1)
      setRemoveOthers(false)
      setError(null)
      const lines: string[] = []
      if (result.removed.length) lines.push(t('audio.package.load.removed', { slots: slotList(result.removed) }))
      if (result.ignored_count) {
        lines.push(t('audio.package.load.ignored', { count: result.ignored_count, names: result.ignored.join(', ') }))
      }
      if (result.warning) lines.push(t('audio.package.load.big', { mb: (result.package.bytes / MB).toFixed(1) }))
      setNotes(lines)
      toast.success(t('audio.package.load.loaded', { count: result.stored.length }))
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'g100-console' && q.queryKey[2] === 'audio',
      })
    },
    onError: (err) => {
      const why = statusOf(err) === 429 ? t('actions.throttled') : filesReason(err)
      setNotes([])
      setError(why ? t('audio.package.load.refused', { reason: why }) : t('audio.package.load.failed'))
    },
  })
  const download = useMutation({
    mutationFn: () => g100ConsoleApi.downloadPackage(),
    onSuccess: (blob: Blob) => saveBlob(blob, 'audio.tar'),
    onError: async (err) => {
      if (statusOf(err) === 404) {
        toast.error(t('audio.package.load.emptyDownload'))
        return
      }
      const why = statusOf(err) === 429 ? t('actions.throttled') : await blobReason(err)
      if (why) toast.error(t('audio.package.load.downloadFailed'), why)
      else toast.error(t('audio.package.load.downloadFailed'))
    },
  })

  const onLoad = () => {
    if (!chosen.length) return
    if (chosen.length > PACKAGE_FILES) {
      setNotes([])
      setError(t('audio.package.load.refused', {
        reason: t('audio.package.load.tooMany', { count: chosen.length, max: PACKAGE_FILES }),
      }))
      return
    }
    const total = chosen.reduce((sum, f) => sum + f.size, 0)
    if (total > PACKAGE_UPLOAD_MAX_BYTES) {
      setNotes([])
      setError(t('audio.package.load.refused', {
        reason: t('audio.package.load.tooBig', { size: total, max: PACKAGE_UPLOAD_MAX_BYTES }),
      }))
      return
    }
    load.mutate({ files: chosen, removeOthers })
  }
  const channels = limits.channels === 1
    ? t('audio.library.channels.mono')
    : t('audio.library.channels.many', { n: limits.channels })

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="g100-package-files" className="text-xs font-medium">{t('audio.package.load.files')}</label>
        <input
          key={inputKey}
          id="g100-package-files"
          type="file"
          multiple
          accept=".tar,.wav,application/x-tar,audio/wav,audio/x-wav"
          disabled={load.isPending}
          onChange={(e) => {
            setChosen(Array.from(e.target.files ?? []))
            setError(null)
            setNotes([])
          }}
          className="max-w-full text-xs"
        />
        <label className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={removeOthers}
            disabled={load.isPending}
            onChange={(e) => setRemoveOthers(e.target.checked)}
          />
          {t('audio.package.load.removeOthers')}
        </label>
        <Button size="sm" onClick={onLoad} disabled={!chosen.length || load.isPending}>
          {load.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {t('audio.package.load.button')}
        </Button>
        {hasFiles && (
          <Button size="sm" variant="outline" onClick={() => download.mutate()} disabled={download.isPending}>
            {download.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('audio.package.load.download')}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {t('audio.package.load.hint', {
          rate: limits.max_sample_rate, channels, bits: limits.bits_per_sample, seconds: limits.max_seconds,
        })}
      </p>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      {notes.map((line) => <p key={line} className="text-xs text-muted-foreground">{line}</p>)}
    </div>
  )
}

function AudioLibrary({ cameraId, overview }: { cameraId: number; overview: G100AudioOverview }) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  const { library, limits } = overview
  const [chosen, setChosen] = useState<File | null>(null)
  const [inputKey, setInputKey] = useState(0)        // remounts the file input to clear it
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [picked, setPicked] = useState<number[]>([])
  const [species, setSpecies] = useState<Record<number, G100Species | ''>>({})
  const [overwrite, setOverwrite] = useState(false)
  const [confirming, setConfirming] = useState<G100AudioFile | null>(null)   // the file to remove

  const reload = () => queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.audio(cameraId) })
  const upload = useMutation({
    mutationFn: (file: File) => g100AudioApi.upload(cameraId, file),
    onSuccess: (row) => {
      setChosen(null)
      setInputKey((k) => k + 1)
      setUploadError(null)
      toast.success(t('audio.library.uploaded', { name: row.file_name }))
      reload()
    },
    onError: (error) => {
      const why = statusOf(error) === 429 ? t('actions.throttled') : fileReason(error)
      setUploadError(why ? t('audio.library.refused', { reason: why }) : t('audio.library.uploadFailed'))
    },
  })
  const remove = useMutation({
    mutationFn: (file: G100AudioFile) => g100AudioApi.remove(cameraId, file.audio_id),
    onSuccess: (_, file) => {
      toast.success(t('audio.library.removed', { name: file.original_name }))
      reload()
    },
    onError: (error) => toast.error(t('audio.library.removeFailed'), refusal(error, t('actions.throttled'))),
    onSettled: () => setConfirming(null),
  })
  const sent = useAudioCommandSent(cameraId)
  const download = useMutation({
    mutationFn: (body: G100AudioDownloadRequest) => g100AudioApi.download(cameraId, body),
    onSuccess: (row) => {
      setPicked([])
      sent.onSuccess(row)
    },
    onError: sent.onError,
  })

  // In library order; a file removed meanwhile drops out.
  const pickedFiles = library.filter((f) => picked.includes(f.audio_id))
  const tooMany = pickedFiles.length > limits.max_per_command
  const toggle = (audioId: number) =>
    setPicked((p) => (p.includes(audioId) ? p.filter((id) => id !== audioId) : [...p, audioId]))
  // Review M3: a file over the server's own limit is refused here, before a byte is sent. The server
  // refuses only above max_bytes and keeps the last word on everything else.
  const onUpload = () => {
    if (!chosen) return
    if (chosen.size > limits.max_bytes) {
      setUploadError(t('audio.library.refused', {
        reason: t('audio.library.tooBig', { size: chosen.size, max: limits.max_bytes }),
      }))
      return
    }
    upload.mutate(chosen)
  }
  // Review M2: the format as the server states it (channels, bit depth, rates, length, size).
  // #358: one rate when the camera takes only one (24 kHz).
  const channels = limits.channels === 1
    ? t('audio.library.channels.mono')
    : t('audio.library.channels.many', { n: limits.channels })
  const rest = {
    seconds: limits.max_seconds, mb: Math.round((limits.max_bytes / MB) * 10) / 10, files: limits.max_files,
  }
  const limitsText = limits.min_sample_rate === limits.max_sample_rate
    ? t('audio.library.limitsOneRate', { channels, bits: limits.bits_per_sample, rate: limits.min_sample_rate, ...rest })
    : t('audio.library.limits', {
      channels, bits: limits.bits_per_sample, minRate: limits.min_sample_rate, maxRate: limits.max_sample_rate, ...rest,
    })
  const wrongRate = t('audio.library.wrongRate', { khz: limits.max_sample_rate / 1000 })
  const onSend = () => download.mutate({
    overwrite,
    audioList: pickedFiles.map((f) => {
      const name = species[f.audio_id]
      return name ? { audioId: f.audio_id, speciesName: name } : { audioId: f.audio_id }
    }),
  })

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t('audio.library.title')}</h3>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="g100-audio-file" className="text-xs font-medium">{t('audio.library.file')}</label>
        <input
          key={inputKey}
          id="g100-audio-file"
          type="file"
          accept=".wav,audio/wav,audio/x-wav,audio/wave"
          onChange={(e) => { setChosen(e.target.files?.[0] ?? null); setUploadError(null) }}
          className="max-w-full text-xs file:mr-2 file:rounded file:border file:border-border file:bg-muted file:px-2 file:py-1 file:text-xs"
        />
        <Button size="sm" onClick={onUpload} disabled={!chosen || upload.isPending}>
          {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {t('audio.library.upload')}
        </Button>
      </div>
      {uploadError && <p role="alert" className="text-xs text-destructive">{uploadError}</p>}
      <p className="text-xs text-muted-foreground">{limitsText}</p>

      {library.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('audio.library.empty')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-border">
            <table className="w-full text-sm" aria-label={t('audio.library.title')}>
              <thead className="bg-muted/50 text-left text-xs">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  <th className="px-2 py-2">{t('audio.library.id')}</th>
                  <th className="px-2 py-2">{t('audio.library.name')}</th>
                  <th className="px-2 py-2">{t('audio.library.size')}</th>
                  <th className="px-2 py-2">{t('audio.library.checksum')}</th>
                  <th className="px-2 py-2">{t('audio.library.duration')}</th>
                  <th className="px-2 py-2">{t('audio.library.speciesColumn')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {library.map((f) => (
                  <tr key={f.audio_id} className="border-t border-border">
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={picked.includes(f.audio_id)}
                        onChange={() => toggle(f.audio_id)}
                        disabled={!f.sample_rate_ok}
                        aria-label={t('audio.library.pick', { name: f.original_name })}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{f.audio_id}</td>
                    <td className="px-2 py-1.5">
                      <div className="break-all text-xs font-medium">{f.original_name}</div>
                      <div className="break-all font-mono text-xs text-muted-foreground">{f.file_name}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs">{`${f.file_size} B`}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{f.checksum}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs">
                      {`${f.duration_seconds.toFixed(1)} s · ${f.sample_rate} Hz`}
                      {/* #358: stored before the 24 kHz rule; the server refuses to send it. */}
                      {!f.sample_rate_ok && (
                        <span className="ml-1 rounded bg-destructive/10 px-1 py-0.5 text-destructive">{wrongRate}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <SpeciesSelect
                        value={species[f.audio_id] ?? ''}
                        onChange={(v) => setSpecies((s) => ({ ...s, [f.audio_id]: v }))}
                        empty={t('audio.speciesNone')}
                        label={t('audio.library.species', { name: f.original_name })}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirming(f)}
                        disabled={remove.isPending}
                        aria-label={t('audio.library.remove', { name: f.original_name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">{t('audio.send.hint')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="h-4 w-4" />
              {t('audio.send.overwrite')}
            </label>
            <Button size="sm" onClick={onSend} disabled={pickedFiles.length === 0 || tooMany || download.isPending}>
              {download.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {t('audio.send.button', { count: pickedFiles.length })}
            </Button>
            {tooMany && (
              <span className="text-xs text-destructive">{t('audio.send.tooMany', { max: limits.max_per_command })}</span>
            )}
          </div>
        </>
      )}
      <RemoveFileDialog
        file={confirming}
        removing={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && remove.mutate(confirming)}
      />
    </div>
  )
}

// Review M4: removing a file cannot be undone (the sound is dropped and its audio ID is never given out
// again), so it is confirmed first. The page's own dialog pattern (TypedNameDialog): titled and described,
// which the shared ConfirmDialog is not (Radix warns about its missing Description).
function RemoveFileDialog({ file, removing, onCancel, onConfirm }: {
  file: G100AudioFile | null
  removing: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('g100Console')
  return (
    <Dialog.Root open={file !== null} onOpenChange={(next) => { if (!next && !removing) onCancel() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <Trash2 className="h-5 w-5 text-destructive" />
            </div>
            <Dialog.Title className="break-all text-lg font-semibold">
              {t('audio.library.confirmTitle', { name: file?.original_name ?? '' })}
            </Dialog.Title>
          </div>
          <Dialog.Description className="text-sm text-muted-foreground">
            {t('audio.library.confirmBody', { id: file?.audio_id ?? '' })}
          </Dialog.Description>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onCancel} disabled={removing}>
              {t('confirm.cancel')}
            </Button>
            <Button variant="destructive" className="flex-1" onClick={onConfirm} disabled={removing}>
              {removing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('audio.library.confirmRemove')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/** One value of the camera's own entry, as it sent it. */
function shown(value: unknown) {
  if (value === undefined || value === null) return '—'
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/** The audioId of a reported entry as a whole number, or null when the camera sent something else. */
function reportedId(value: unknown): number | null {
  const id = typeof value === 'number' ? value : typeof value === 'string' && /^\d{1,10}$/.test(value) ? Number(value) : NaN
  return Number.isInteger(id) && id >= 0 ? id : null
}

function AudioOnCamera({ onCamera, onPlay, onStop, inFlight }: {
  onCamera: G100AudioOnCamera
  onPlay: (audioId: number) => void
  onStop: (fallback: number) => void       // #380: the id Stop carries when nothing was played from here
  inFlight: G100AudioPlaybackRequest | null // the playback being queued right now, if any
}) {
  const { t } = useTranslation('g100Console')
  const pending = inFlight !== null
  const firstReported = onCamera.audio_list.map((e) => reportedId(e.audioId)).find((id) => id !== null) ?? 1
  return (
    <div className="min-w-0 space-y-2">
      <h3 className="text-sm font-semibold">{t('audio.onCamera.title')}</h3>
      {onCamera.package_status !== null && (
        <p className="text-xs text-muted-foreground">
          {t('audio.onCamera.package', {
            status: PACKAGE_STATUS.has(onCamera.package_status)
              ? t(`audio.onCamera.packageStatus.${onCamera.package_status as (typeof PACKAGE_STATUSES)[number]}`)
              : onCamera.package_status,
            when: when(onCamera.package_status_at) ?? t('status.none'),
          })}
        </p>
      )}
      {!onCamera.reported ? (
        <p className="text-sm text-muted-foreground">{t('audio.onCamera.notReported')}</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {t('audio.onCamera.count', {
              // audioCount as the camera sent it; without one, the entries it listed.
              count: onCamera.audio_count ?? onCamera.audio_list.length,
              when: when(onCamera.reported_at) ?? t('status.none'),
            })}
          </p>
          {onCamera.audio_list.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('audio.onCamera.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded border border-border">
              <table className="w-full text-xs" aria-label={t('audio.onCamera.title')}>
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-2 py-2">{t('audio.onCamera.id')}</th>
                    <th className="px-2 py-2">{t('audio.onCamera.fileName')}</th>
                    <th className="px-2 py-2">{t('audio.onCamera.species')}</th>
                    <th className="px-2 py-2">{t('audio.onCamera.other')}</th>
                    <th className="px-2 py-2"><span className="sr-only">{t('audio.playback.playTyped')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {onCamera.audio_list.map((entry, i) => {
                    const { audioId, fileName, speciesName, ...rest } = entry
                    const id = reportedId(audioId)
                    return (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1.5 font-mono">{shown(audioId)}</td>
                        <td className="break-all px-2 py-1.5 font-mono">{shown(fileName)}</td>
                        <td className="px-2 py-1.5">{shown(speciesName)}</td>
                        <td className="break-all px-2 py-1.5 font-mono text-muted-foreground">
                          {Object.entries(rest).map(([k, v]) => `${k}: ${shown(v)}`).join(', ')}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {/* #380: play this sound once (audioPlayback type 1). */}
                          {id !== null && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onPlay(id)}
                              disabled={pending}
                              aria-label={t('audio.playback.play', { id })}
                            >
                              {inFlight?.type === 1 && inFlight.audioId === id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Play className="h-4 w-4" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {/* #380: Stop (audioPlayback type 0) and what the camera's status means. Offered also before the
          camera has reported a list: a sound played by a typed id can be stopped the same way. */}
      <div className="flex flex-wrap items-start gap-3">
        <Button size="sm" variant="outline" onClick={() => onStop(firstReported)} disabled={pending}>
          {inFlight?.type === 0 ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Square className="mr-2 h-4 w-4" />}
          {t('audio.playback.stop')}
        </Button>
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">{t('audio.playback.legend')}</p>
      </div>
    </div>
  )
}

/** The one audio ID typed, or null: audioPlayback (6.12.23) plays "any single warning sound". */
function singleTypedId(text: string): number | null {
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean)
  return parts.length === 1 && /^\d{1,10}$/.test(parts[0]) ? Number(parts[0]) : null
}

function AudioByIds({ cameraId, limits, onPlay, inFlight }: {
  cameraId: number
  limits: G100AudioLimits
  onPlay: (audioId: number) => void      // #380
  inFlight: G100AudioPlaybackRequest | null
}) {
  const { t } = useTranslation('g100Console')
  const [ids, setIds] = useState('')
  const [speciesName, setSpeciesName] = useState<G100Species | ''>('')
  const [force, setForce] = useState(false)
  const [idsError, setIdsError] = useState<string | null>(null)

  const sent = useAudioCommandSent(cameraId)
  const speciesSet = useMutation({
    mutationFn: (body: G100AudioSpeciesRequest) => g100AudioApi.setSpecies(cameraId, body),
    ...sent,
  })
  const cameraDelete = useMutation({
    mutationFn: (body: G100AudioDeleteRequest) => g100AudioApi.deleteFromCamera(cameraId, body),
    ...sent,
  })

  /** The typed audio IDs, or null (and why) when they are not 1..max whole numbers. The server
   *  has the last word: a repeated or out-of-range ID comes back as its refusal. */
  const parsed = (): number[] | null => {
    const parts = ids.split(',').map((p) => p.trim()).filter(Boolean)
    if (parts.length === 0 || parts.length > limits.max_per_command || parts.some((p) => !/^\d{1,10}$/.test(p))) {
      setIdsError(t('audio.camera.badIds', { max: limits.max_per_command }))
      return null
    }
    setIdsError(null)
    return parts.map(Number)
  }
  const onSetSpecies = () => {
    const list = parsed()
    if (list && speciesName) speciesSet.mutate({ audioList: list.map((audioId) => ({ audioId, speciesName })) })
  }
  const onDelete = () => {
    const list = parsed()
    if (list) cameraDelete.mutate({ force, audioList: list.map((audioId) => ({ audioId })) })
  }
  const typedId = singleTypedId(ids)
  const pending = inFlight !== null
  const typedInFlight = inFlight?.type === 1 && typedId !== null && inFlight.audioId === typedId

  return (
    <div className="min-w-0 space-y-3">
      <h3 className="text-sm font-semibold">{t('audio.camera.title')}</h3>
      <div className="space-y-1">
        <label htmlFor="g100-audio-ids" className="block text-xs font-medium">{t('audio.camera.ids')}</label>
        <input
          id="g100-audio-ids"
          value={ids}
          onChange={(e) => setIds(e.target.value)}
          placeholder="1, 2"
          autoComplete="off"
          className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-xs"
        />
        <p className="text-xs text-muted-foreground">{t('audio.camera.hint')}</p>
        {idsError && <p className="text-xs text-destructive">{idsError}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="g100-audio-species" className="text-xs font-medium">{t('audio.camera.species')}</label>
        <SpeciesSelect
          id="g100-audio-species"
          value={speciesName}
          onChange={setSpeciesName}
          empty={t('audio.camera.chooseSpecies')}
        />
        <Button size="sm" variant="outline" onClick={onSetSpecies} disabled={!speciesName || speciesSet.isPending}>
          {speciesSet.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('audio.camera.setSpecies')}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="h-4 w-4" />
          {t('audio.camera.force')}
        </label>
        <Button size="sm" variant="outline" onClick={onDelete} disabled={cameraDelete.isPending}>
          {cameraDelete.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
          {t('audio.camera.delete')}
        </Button>
        {/* #380: play the one typed id (audioPlayback type 1); the box otherwise takes up to max ids. */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => typedId !== null && onPlay(typedId)}
          disabled={typedId === null || pending}
          title={t('audio.playback.oneId')}
        >
          {typedInFlight ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
          {t('audio.playback.playTyped')}
        </Button>
      </div>
    </div>
  )
}

// ---- results ---------------------------------------------------------------------------

const STATE_STYLE: Record<G100ConsoleCommand['state'], string> = {
  pending: 'bg-muted text-muted-foreground',
  sent: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  done: 'bg-green-500/15 text-green-700 dark:text-green-400',
  failed: 'bg-red-500/15 text-red-700 dark:text-red-400',
  expired: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  superseded: 'bg-muted text-muted-foreground',
}

function ResultsCard({ cameraId }: { cameraId: number }) {
  const { t } = useTranslation('g100Console')
  const commands = useQuery({
    queryKey: queryKeys.g100Console.commands(cameraId),
    queryFn: () => g100ConsoleApi.commands(cameraId),
    // Polled only while a command still waits to be sent or answered.
    refetchInterval: (query) =>
      query.state.data?.results.some((r) => r.open) ? queryOptions.g100Console.commandsWhileOpenMs : false,
  })
  const rows = commands.data?.results ?? []
  const waiting = rows.some((r) => r.open)

  return (
    <Card>
      <section aria-label={t('results.title')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('results.title')}</CardTitle>
          {waiting && <p className="text-xs text-muted-foreground">{t('results.waiting')}</p>}
        </CardHeader>
        <CardContent>
          {commands.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : commands.isError ? (
            <p className="text-sm text-destructive">{t('results.loadError')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('results.empty')}</p>
          ) : (
            <ul className="max-h-[28rem] space-y-2 overflow-y-auto">
              {rows.map((r) => <ResultRow key={r.id} row={r} />)}
            </ul>
          )}
        </CardContent>
      </section>
    </Card>
  )
}

function ResultRow({ row }: { row: G100ConsoleCommand }) {
  const { t } = useTranslation('g100Console')
  const hasParams = Object.keys(row.params ?? {}).length > 0
  const ota = otaText(row)
  return (
    <li className="rounded border border-border/60 p-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-semibold">{row.action}</span>
        <span className={cn('rounded px-1.5 py-0.5', STATE_STYLE[row.state])}>{ota ? t(`results.ota.${ota.key}`, ota.vars) : t(`results.state.${row.state}`)}</span>
        {row.result_code !== null && !ota && (
          <span className="font-mono">{t('results.code', { code: row.result_code })}</span>
        )}
        <span className="ml-auto text-muted-foreground">{when(row.answered_at ?? row.sent_at ?? row.created_at)}</span>
      </div>
      {hasParams && (
        <div className="mt-1">
          <span className="text-muted-foreground">{t('results.params')}</span>
          <pre className="whitespace-pre-wrap break-all font-mono">{pretty(row.params)}</pre>
        </div>
      )}
      {row.result && (
        <div className="mt-1">
          <span className="text-muted-foreground">{t('results.result')}</span>
          <pre className="whitespace-pre-wrap break-all font-mono">{pretty(row.result)}</pre>
        </div>
      )}
    </li>
  )
}

// ---- message log -----------------------------------------------------------------------

function timeWithMs(iso: string) {
  const d = new Date(iso)
  return `${d.toLocaleString()}.${String(d.getMilliseconds()).padStart(3, '0')}`
}

function prettyPayload(payload: string) {
  try {
    return pretty(JSON.parse(payload))
  } catch {
    return payload
  }
}

function MessageLog({ cameraId }: { cameraId: number }) {
  const { t } = useTranslation('g100Console')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<number | null>(null)
  const messages = useQuery({
    queryKey: queryKeys.g100Console.messages(cameraId, page),
    queryFn: () => g100ConsoleApi.messages(cameraId, { page }),
    placeholderData: (prev) => prev,
    ...queryOptions.g100Console.messages,
  })
  const rows = messages.data?.results ?? []
  const total = messages.data?.count ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <Card>
      <section aria-label={t('log.title')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('log.title')}</CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('log.total', { total })} · {t('log.live')}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {messages.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : messages.isError ? (
            <p className="text-sm text-destructive">{t('log.loadError')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('log.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2">{t('log.time')}</th>
                    <th className="px-3 py-2">{t('log.direction')}</th>
                    <th className="px-3 py-2">{t('log.channel')}</th>
                    <th className="px-3 py-2">{t('log.result')}</th>
                    <th className="px-3 py-2">{t('log.what')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <MessageRow key={r.id} row={r} open={open === r.id}
                                onToggle={() => setOpen(open === r.id ? null : r.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pages > 1 && (
            <div className="flex items-center gap-3 text-sm">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                {t('log.prev')}
              </Button>
              <span>{t('log.page', { page, pages })}</span>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                {t('log.next')}
              </Button>
            </div>
          )}
        </CardContent>
      </section>
    </Card>
  )
}

function MessageRow({ row, open, onToggle }: { row: G100ConsoleMessage; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('g100Console')
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer border-t border-border hover:bg-muted/30">
        <td className="whitespace-nowrap px-3 py-2 text-xs">{timeWithMs(row.created_at)}</td>
        <td className="whitespace-nowrap px-3 py-2 text-xs">
          {row.direction === 'out' ? t('log.toCamera') : t('log.fromCamera')}
        </td>
        <td className="px-3 py-2 text-xs">{row.channel}</td>
        <td className="px-3 py-2 text-xs">
          {row.ok === null ? '—' : (
            <span className={cn('rounded px-1.5 py-0.5',
              row.ok ? 'bg-green-500/15 text-green-700 dark:text-green-400'
                     : 'bg-red-500/15 text-red-700 dark:text-red-400')}>
              {row.ok ? t('log.ok') : t('log.refused')}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs">{row.summary}</td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={5} className="space-y-2 px-3 py-3">
            <div>
              <div className="text-xs font-semibold text-muted-foreground">{t('log.topic')}</div>
              <div className="break-all font-mono text-xs">{row.topic || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground">{t('log.payload')}</div>
              <pre className="whitespace-pre-wrap break-all font-mono text-xs">
                {row.payload ? prettyPayload(row.payload) : '—'}
              </pre>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
